import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertOctagon, CalendarClock, TrendingUp, Flame, Clock, ArrowRight,
  CheckSquare, Contact as ContactIcon, Building2, Calendar as CalIcon, Sparkles, Target,
} from "lucide-react";
import { api } from "@/lib/api";

const STAGE_LABEL = {
  new: "New", qualified: "Qualified", proposal: "Proposal",
  negotiation: "Negotiation", won: "Won", lost: "Lost",
};

const PRIORITY_TONE = {
  high: "text-brand-red bg-red-500/10 ring-red-500/30",
  medium: "text-brand-orange bg-orange-500/10 ring-orange-500/30",
  low: "text-muted-foreground bg-muted ring-border",
};

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtMoney(cur, val) {
  const n = Number(val || 0);
  const opts = { maximumFractionDigits: n === Math.floor(n) ? 0 : 2 };
  return `${cur} ${n.toLocaleString(undefined, opts)}`;
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function WorkspacePulse() {
  const [data, setData] = useState(null);
  const [primaryGoal, setPrimaryGoal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [cc, g] = await Promise.all([
        api.get("/command-center/summary"),
        api.get("/goals").catch(() => ({ data: { goals: [] } })),
      ]);
      setData(cc.data);
      // Choose the highest-priority active goal as the primary
      const priorityRank = { high: 3, medium: 2, low: 1 };
      const activeGoals = (g.data.goals || []).filter((x) => x.status === "active");
      activeGoals.sort((a, b) => (priorityRank[b.priority] || 0) - (priorityRank[a.priority] || 0));
      setPrimaryGoal(activeGoals[0] || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="pulse-loading">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-40 animate-pulse rounded-2xl bg-muted" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  const {
    today = [], overdue = [], upcoming = [], deals_needing_followup = [],
    pipeline_by_currency = [], recent_activity = [], upcoming_events = [],
    counts = {},
  } = data;

  const anyContent =
    today.length + overdue.length + upcoming.length + deals_needing_followup.length +
    pipeline_by_currency.length + recent_activity.length + upcoming_events.length > 0;

  if (!anyContent) {
    return (
      <div className="rounded-3xl border border-dashed border-border bg-card p-8 text-center" data-testid="pulse-empty">
        <p className="font-display text-lg font-semibold tracking-tight">Your workspace is quiet.</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Add your first task, deal or event to see it here — or ask your AI to do it for you below.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2 text-sm">
          <QuickLink to="/app/work" icon={CheckSquare} label="Open Work" />
          <QuickLink to="/app/sales" icon={TrendingUp} label="Open Sales" />
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="workspace-pulse">
      {primaryGoal && (
        <Card
          testid="pulse-primary-goal"
          title="Primary Goal"
          icon={Target}
          accent="text-brand-teal"
          className="md:col-span-2 xl:col-span-3"
          cta={<CardCta to="/app/goals">Open goals</CardCta>}
        >
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <p className="font-display text-base font-semibold sm:text-lg">{primaryGoal.name}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                <span>{primaryGoal.category}</span>
                {primaryGoal.target && <span>Target: {primaryGoal.target} {primaryGoal.target_unit}</span>}
                {primaryGoal.target_date && <span>by {primaryGoal.target_date.slice(0, 10)}</span>}
                {(() => {
                  const stage = (primaryGoal.stages || []).find((s) => (s.milestones || []).some((m) => !m.done));
                  return stage ? <span>Current stage: <b className="text-foreground">{stage.name}</b></span> : null;
                })()}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-brand-teal" style={{ width: `${primaryGoal.progress || 0}%` }} />
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground">{primaryGoal.progress || 0}%</span>
              </div>
              {(() => {
                const stage = (primaryGoal.stages || []).find((s) => (s.milestones || []).some((m) => !m.done));
                const nextMs = stage?.milestones?.find((m) => !m.done);
                return nextMs ? <p className="mt-2 text-xs"><span className="font-semibold text-brand-teal">Next milestone:</span> {nextMs.name}</p> : null;
              })()}
            </div>
          </div>
        </Card>
      )}

      <Card
        testid="pulse-today"
        title="Today's priorities"
        icon={Flame}
        accent="text-brand-orange"
        count={counts.today || today.length}
        cta={<CardCta to="/app/work">Open work</CardCta>}
      >
        {today.length === 0 ? (
          <Empty>No tasks due today. </Empty>
        ) : (
          <ul className="space-y-2">
            {today.slice(0, 5).map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{t.title}</span>
                <PriorityPill p={t.priority} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        testid="pulse-overdue"
        title="Overdue"
        icon={AlertOctagon}
        accent="text-brand-red"
        count={counts.overdue || overdue.length}
        cta={<CardCta to="/app/work">Review</CardCta>}
      >
        {overdue.length === 0 ? (
          <Empty>Nothing overdue. Nicely done.</Empty>
        ) : (
          <ul className="space-y-2">
            {overdue.slice(0, 5).map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{t.title}</span>
                <span className="text-xs text-brand-red">{fmtDate(t.due_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        testid="pulse-upcoming"
        title="Upcoming (7 days)"
        icon={Clock}
        accent="text-brand-blue"
        count={upcoming.length}
        cta={<CardCta to="/app/work">Plan week</CardCta>}
      >
        {upcoming.length === 0 ? (
          <Empty>Nothing scheduled in the week.</Empty>
        ) : (
          <ul className="space-y-2">
            {upcoming.slice(0, 5).map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{t.title}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(t.due_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        testid="pulse-followups"
        title="Deals needing follow-up"
        icon={ContactIcon}
        accent="text-brand-teal"
        count={deals_needing_followup.length}
        cta={<CardCta to="/app/sales">Open pipeline</CardCta>}
      >
        {deals_needing_followup.length === 0 ? (
          <Empty>No follow-ups due today.</Empty>
        ) : (
          <ul className="space-y-2">
            {deals_needing_followup.slice(0, 5).map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{d.title}</span>
                <span className="text-xs text-muted-foreground">{STAGE_LABEL[d.stage] || d.stage}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        testid="pulse-pipeline"
        title="Pipeline by currency"
        icon={TrendingUp}
        accent="text-brand-green"
        count={counts.active_deals || pipeline_by_currency.reduce((s, p) => s + p.count, 0)}
        cta={<CardCta to="/app/sales">Open pipeline</CardCta>}
      >
        {pipeline_by_currency.length === 0 ? (
          <Empty>No active deals.</Empty>
        ) : (
          <ul className="space-y-2" data-testid="pulse-pipeline-list">
            {pipeline_by_currency.map((p) => (
              <li key={p.currency} className="flex items-center justify-between gap-2 text-sm" data-testid={`pulse-pipeline-${p.currency}`}>
                <span className="text-muted-foreground">{p.count} deal{p.count === 1 ? "" : "s"}</span>
                <span className="font-semibold">{fmtMoney(p.currency, p.value)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[10px] text-muted-foreground">Totals are never combined across currencies.</p>
      </Card>

      <Card
        testid="pulse-events"
        title="Upcoming commitments"
        icon={CalendarClock}
        accent="text-brand-blue"
        count={upcoming_events.length}
        cta={<CardCta to="/app/calendar">Open calendar</CardCta>}
      >
        {upcoming_events.length === 0 ? (
          <Empty>No events scheduled.</Empty>
        ) : (
          <ul className="space-y-2">
            {upcoming_events.slice(0, 5).map((e) => (
              <li key={e.id} className="flex items-start justify-between gap-2 text-sm">
                <span className="truncate">{e.title}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(e.starts_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        testid="pulse-activity"
        title="Recent activity"
        icon={Sparkles}
        accent="text-brand-teal"
        count={recent_activity.length}
        className="md:col-span-2 xl:col-span-3"
      >
        {recent_activity.length === 0 ? (
          <Empty>Nothing yet. Once you or your AI adds records, they'll show up here.</Empty>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {recent_activity.map((r) => (
              <li key={r.kind + r.id} className="flex items-center gap-2 rounded-lg bg-background/60 px-3 py-2 text-sm">
                <ActivityIcon kind={r.kind} />
                <div className="min-w-0 flex-1">
                  <p className="truncate">{r.label}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {r.kind}
                    {r.stage ? ` · ${STAGE_LABEL[r.stage] || r.stage}` : ""}
                    {r.status ? ` · ${r.status}` : ""}
                    {r.currency ? ` · ${r.currency} ${Number(r.value || 0).toLocaleString()}` : ""}
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground">{relTime(r.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Card({ title, icon: Icon, accent, count, cta, className = "", testid, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex flex-col rounded-2xl border border-border bg-card p-5 ${className}`}
      data-testid={testid}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${accent}`} />
          <h3 className="font-display text-sm font-semibold tracking-tight">{title}</h3>
          {typeof count === "number" && count > 0 && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{count}</span>
          )}
        </div>
        {cta}
      </div>
      <div className="flex-1">{children}</div>
    </motion.section>
  );
}

function CardCta({ to, children }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground">
      {children} <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

function PriorityPill({ p = "medium" }) {
  return <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 ${PRIORITY_TONE[p] || PRIORITY_TONE.medium}`}>{p}</span>;
}

function Empty({ children }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
      <Icon className="h-3.5 w-3.5" /> {label}
    </Link>
  );
}

function ActivityIcon({ kind }) {
  const map = { task: CheckSquare, deal: TrendingUp, company: Building2, contact: ContactIcon, event: CalIcon };
  const I = map[kind] || Sparkles;
  return <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-muted"><I className="h-3 w-3 text-muted-foreground" /></span>;
}
